import { useState, useMemo } from 'react';
import {
  X,
  Clock,
  Activity,
  BookOpen,
  Loader2,
  RotateCcw,
  MessageCircle,
  Mail,
  MapPin,
  Calendar,
  CheckCircle2,
  User as UserIcon,
  Phone,
  Edit2,
  Check,
  Hash,
  Copy,
  GraduationCap,
  History,
  Info,
  KeyRound,
} from 'lucide-react';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';
import {
  useRecordParticipantPasswordReset,
  useUserDetails,
  useUpdateUser,
} from '../hooks/useUsers';
import { useLanguage } from '../lib/LanguageContext';
import { useResetAttempts } from '../hooks/useEvaluations';
import { useEnrollments } from '../hooks/useEnrollments';
import { useNotificationStore } from '../store/notifications';
import { CityAutocomplete } from './CityAutocomplete';
import {
  CommunicationModal,
  type ChannelType,
  type CommunicationRecipient,
} from './CommunicationModal';
import { Button } from '@lms/ui';
import { getNeonAuthClient } from '../lib/neon-auth';
import { getCrossAppUrl } from '@lms/shared';
import { ParticipantPasswordResetDialog } from './ParticipantPasswordResetDialog';

type ActiveTab = 'profile' | 'programs' | 'activities';

/**
 * Lightweight `formatDistanceToNow` replacement.
 */
function formatDistanceToNow(
  date: Date,
  language: 'id' | 'ar',
  options: { addSuffix?: boolean } = {}
): string {
  const suffix = options.addSuffix;
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.max(0, Math.floor(diffMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  let core: string;
  if (seconds < 45) {
    core = language === 'ar' ? 'لحظات' : 'beberapa detik';
  } else if (minutes < 45) {
    core = language === 'ar' ? `${minutes} دقيقة` : `${minutes} menit`;
  } else if (hours < 22) {
    core = language === 'ar' ? `${hours} ساعة` : `${hours} jam`;
  } else if (days < 26) {
    core = language === 'ar' ? `${days} يوم` : `${days} hari`;
  } else if (months < 11) {
    core = language === 'ar' ? `${months} شهر` : `${months} bulan`;
  } else {
    core = language === 'ar' ? `${years} سنة` : `${years} tahun`;
  }

  if (!suffix) return core;
  return language === 'ar' ? `منذ ${core}` : `${core} yang lalu`;
}

interface EnrollmentItem {
  id: string;
  programId?: string;
  status: string;
  progress?: number;
  startedAt?: string;
  completedAt?: string;
  program?: {
    id?: string;
    name: string;
    code?: string;
    cohort?: number;
  };
}

interface RegistrationItem {
  id: string;
  status: string;
  domisili?: string;
  reason?: string;
  registeredAt: string;
  program?: {
    id: string;
    name: string;
    code?: string;
  };
  participantId?: {
    participantCode: string;
  } | null;
}

interface EvaluationItem {
  id: string;
  title: string;
  passingScore: number;
}

interface EvaluationResult {
  score?: number;
  metadata?: Record<string, unknown> | null;
}

interface AuditLogItem {
  id: string;
  action: string;
  createdAt: string;
  entity: string;
}

export function UserDetailDrawer({
  userId,
  programId,
  onClose,
}: {
  userId: string;
  programId?: string;
  onClose: () => void;
}) {
  const { language, t } = useLanguage();
  const { data: user, isLoading } = useUserDetails(userId);
  // Always scope by userId — an unscoped call would list every enrollment
  // in the system (300k+ rows) and crash the API with a stack overflow.
  const { data: userEnrollments } = useEnrollments(programId ? { userId, programId } : { userId });
  const userEnrollment = userEnrollments?.[0];
  const { mutate: resetAttempts, isPending: isResetting } = useResetAttempts();
  const { mutate: updateUser, isPending: isUpdatingUser } = useUpdateUser();
  const { mutateAsync: recordPasswordResetRequest } = useRecordParticipantPasswordReset();
  const { addNotification } = useNotificationStore();

  // Navigation tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('profile');

  // Communication modal state
  const [commChannel, setCommChannel] = useState<'whatsapp' | 'email' | null>(null);
  const [commTemplateId, setCommTemplateId] = useState<string | undefined>();

  // Domisili inline editing state
  const [isEditingDomisili, setIsEditingDomisili] = useState(false);
  const [selectedDomisili, setSelectedDomisili] = useState('');
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);

  // Extract domisili from user metadata or registrations
  const currentDomisili = useMemo(() => {
    if (!user) return '';
    const regDomisili = user.registrations?.find((r: RegistrationItem) => r.domisili)?.domisili;
    if (regDomisili) return regDomisili;
    const meta = (user.legacyMetadata as Record<string, unknown>) || {};
    if (typeof meta.domisili === 'string' && meta.domisili) return meta.domisili;
    if (typeof meta.city === 'string' && meta.city) return meta.city;
    if (typeof meta.kabupaten === 'string' && meta.kabupaten) return meta.kabupaten;
    return '';
  }, [user]);

  // Extract active NIP / Participant Code
  const activeNip = useMemo(() => {
    if (!user) return null;
    const fromReg = user.registrations?.find(
      (r: RegistrationItem) => r.participantId?.participantCode
    )?.participantId?.participantCode;
    return fromReg || user.legacyNip || null;
  }, [user]);

  // Primary Program Name
  const primaryProgramName = useMemo(() => {
    if (!user) return undefined;
    return user.enrollments?.[0]?.program?.name || user.registrations?.[0]?.program?.name;
  }, [user]);

  const roleName =
    user?.roles?.[0] && typeof user.roles[0] === 'object' && 'role' in user.roles[0]
      ? (user.roles[0] as { role: { name: string } }).role.name
      : 'USER';
  const isParticipant = roleName === 'PARTICIPANT';

  const handleCopyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addNotification({
        type: 'success',
        title: t(copy.users.toastCopied),
        message: `${label} berhasil disalin ke clipboard.`,
      });
    } catch {
      // ignore
    }
  };

  const handleResetAttempts = (evaluationId: string) => {
    const message =
      language === 'ar' ? copy.users.confirmResetAttempts.ar : copy.users.confirmResetAttempts.id;
    if (confirm(message)) {
      resetAttempts({ evaluationId, userId });
    }
  };

  const handleSaveDomisili = () => {
    if (!userId) return;
    updateUser(
      {
        id: userId,
        data: {
          domisili: selectedDomisili.trim(),
        },
      },
      {
        onSuccess: () => {
          setIsEditingDomisili(false);
          addNotification({
            type: 'success',
            title: t(copy.users.toastDomisiliUpdated),
            message: `Wilayah domisili peserta berhasil diperbarui ke ${selectedDomisili}.`,
          });
        },
      }
    );
  };

  const handlePasswordReset = async () => {
    if (!user) return;

    const participantLoginUrl = getCrossAppUrl('participant', {
      participantUrl: import.meta.env.VITE_PARTICIPANT_URL,
    });
    const redirectTo = new URL('/reset-password', participantLoginUrl).toString();
    const response = await getNeonAuthClient().requestPasswordReset({
      email: user.email,
      redirectTo,
    });
    if (response.error) {
      throw new Error(response.error.message);
    }

    await recordPasswordResetRequest(user.id);
    addNotification({
      type: 'success',
      title: t(copy.users.passwordReset.successTitle),
      message: t(copy.users.passwordReset.successMessage).replace('{email}', user.email),
    });
  };

  const openCommunication = (channel: ChannelType, templateId?: string) => {
    setCommTemplateId(templateId);
    setCommChannel(channel);
  };

  const sharePasswordResetInstructions = (channel: ChannelType) => {
    setIsPasswordResetOpen(false);
    openCommunication(channel, 'password_reset_sent');
  };

  const commRecipient: CommunicationRecipient | null = user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone || undefined,
        gender: user.gender as 'MALE' | 'FEMALE' | undefined,
        programName: primaryProgramName,
        domisili: currentDomisili || undefined,
        participantCode: activeNip || undefined,
      }
    : null;

  // Compute Last Activity
  const lastActivityDate = user?.auditLogs?.[0]?.createdAt;

  // Total Programs count
  const totalProgramsCount = (user?.enrollments?.length || 0) + (user?.registrations?.length || 0);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-on-surface/40 p-0 backdrop-blur-xl animate-fadeIn sm:p-4">
        {/* Drawer Shell */}
        <div className="flex h-full w-full flex-col overflow-hidden bg-surface-container-lowest shadow-level5 animate-slideLeft sm:h-[calc(100vh-2rem)] sm:max-w-4xl sm:rounded-[2rem] xl:max-w-5xl">
          {/* 1. Header Profile Banner */}
          <div className="bg-surface-container-low p-5 sm:p-7">
            <div className="flex justify-between items-start gap-4">
              <div className="flex items-center gap-4 min-w-0">
                {/* Avatar with Gender Color Indicator */}
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl font-headline text-xl font-bold ${
                    user?.gender === 'FEMALE'
                      ? 'bg-tertiary/15 text-tertiary'
                      : user?.gender === 'MALE'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-surface-container-highest text-on-surface-variant'
                  }`}
                >
                  {user?.name ? user.name.charAt(0).toUpperCase() : <UserIcon size={24} />}
                </div>

                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-headline font-bold text-on-surface m-0 tracking-tight truncate">
                    {user?.name || t(copy.users.detailTitle)}
                  </h2>

                  {/* Badges Bar */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {/* Role Badge */}
                    <span
                      className={`text-[10px] font-label font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-lg ${
                        roleName === 'ADMIN'
                          ? 'bg-tertiary/15 text-tertiary'
                          : roleName === 'TEACHER'
                            ? 'bg-secondary/25 text-on-surface'
                            : roleName === 'PARTICIPANT'
                              ? 'bg-success-container text-primary'
                              : 'bg-surface-container-high text-on-surface'
                      }`}
                    >
                      {roleName}
                    </span>

                    {/* Gender Badge (Ikhwan / Akhwat) */}
                    {user?.gender && (
                      <span
                        className={`text-[10px] font-label font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                          user.gender === 'FEMALE'
                            ? 'bg-tertiary/15 text-tertiary'
                            : 'bg-primary/10 text-primary'
                        }`}
                      >
                        {user.gender === 'FEMALE' ? 'Akhwat' : 'Ikhwan'}
                      </span>
                    )}

                    {/* Active Status Badge */}
                    <span
                      className={`text-[10px] font-label font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                        user?.isActive
                          ? 'bg-success-container text-primary'
                          : 'bg-surface-container-high text-on-surface-variant'
                      }`}
                    >
                      {user?.isActive ? 'Aktif' : 'Non-Aktif'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                aria-label={t(copy.users.btnClose)}
                className="rounded-2xl p-2 text-on-surface-variant transition-all hover:bg-surface-container-high hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
              >
                <X size={20} />
              </button>
            </div>

            {/* Quick NIP Bar if available */}
            {activeNip && (
              <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl bg-surface-container-highest/70 px-3.5 py-2.5">
                <div className="flex items-center gap-1.5 text-xs text-on-surface-variant font-medium">
                  <Hash size={14} className="text-primary" />
                  <Bilingual {...copy.users.nipCodeLabel} />:
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-lg bg-primary/10 px-2.5 py-1 font-mono text-xs font-bold text-primary">
                    {activeNip}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopyText(activeNip, 'NIP Peserta')}
                    className="rounded-lg p-1 text-on-surface-variant transition-colors hover:bg-surface-container-lowest hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20"
                    title={t(copy.users.btnCopyData)}
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 2. Quick Communication Actions Bar */}
          {user && (
            <div className="flex flex-wrap items-center gap-3 bg-surface-container-lowest px-5 py-3 sm:px-7">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => openCommunication('whatsapp')}
                leftIcon={<MessageCircle size={15} />}
                className="min-w-44 flex-1 justify-center rounded-xl bg-primary py-2 text-xs font-bold text-on-primary transition-colors hover:bg-primary-container active:scale-95"
              >
                <Bilingual {...copy.users.btnContactWA} />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openCommunication('email')}
                leftIcon={<Mail size={15} />}
                className="min-w-36 flex-1 justify-center rounded-xl bg-surface-container-high py-2 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-highest active:scale-95"
              >
                <Bilingual {...copy.users.btnContactEmail} />
              </Button>
              {isParticipant && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPasswordResetOpen(true)}
                  leftIcon={<KeyRound size={15} />}
                  className="min-w-44 flex-1 justify-center rounded-xl bg-surface-container-high py-2 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-highest active:scale-95"
                >
                  <Bilingual {...copy.users.passwordReset.quickAction} />
                </Button>
              )}
            </div>
          )}

          {/* 3. Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto bg-surface-container-low px-5 py-2 sm:px-7">
            <button
              type="button"
              onClick={() => setActiveTab('profile')}
              className={`flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-bold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 ${
                activeTab === 'profile'
                  ? 'bg-surface-container-lowest text-primary shadow-level1'
                  : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
              }`}
            >
              <Activity size={15} />
              <Bilingual {...copy.users.tabProfile} />
            </button>

            {isParticipant && (
              <button
                type="button"
                onClick={() => setActiveTab('programs')}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-bold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 ${
                  activeTab === 'programs'
                    ? 'bg-surface-container-lowest text-primary shadow-level1'
                    : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
                }`}
              >
                <GraduationCap size={15} />
                <Bilingual {...copy.users.tabPrograms} />
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                  {totalProgramsCount}
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setActiveTab('activities')}
              className={`flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-bold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 ${
                activeTab === 'activities'
                  ? 'bg-surface-container-lowest text-primary shadow-level1'
                  : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
              }`}
            >
              <History size={15} />
              <Bilingual {...copy.users.tabActivities} />
              {user?.auditLogs && user.auditLogs.length > 0 && (
                <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-[10px] font-bold text-on-surface-variant">
                  {user.auditLogs.length}
                </span>
              )}
            </button>
          </div>

          {/* 4. Tab Content Area */}
          <div className="flex-1 overflow-y-auto bg-surface p-5 sm:p-7 custom-scrollbar">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-48 space-y-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : user ? (
              <>
                {/* TAB 1: IDENTITAS & PROFIL */}
                {activeTab === 'profile' && (
                  <div className="space-y-6 animate-fadeIn">
                    <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)]">
                      {/* Card 1: Data Kontak & Identitas */}
                      <div className="space-y-4 rounded-3xl bg-surface-container-lowest p-5 sm:p-6">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-label font-bold text-on-surface flex items-center gap-1.5">
                            <Info size={15} className="text-primary" /> Informasi Kontak
                          </span>
                        </div>

                        {/* Email */}
                        <div className="flex items-start justify-between gap-3 rounded-2xl bg-surface-container-low p-3.5">
                          <div>
                            <div className="text-[11px] text-on-surface-variant font-medium flex items-center gap-1">
                              <Mail size={12} className="text-primary" />{' '}
                              <Bilingual {...copy.users.labelEmailShort} />
                            </div>
                            <div className="font-body text-xs font-semibold text-on-surface mt-0.5 flex items-center gap-2">
                              <span>{user.email}</span>
                              <button
                                type="button"
                                onClick={() => handleCopyText(user.email, 'Email')}
                                className="text-on-surface-variant hover:text-primary"
                                title={t(copy.users.btnCopyData)}
                              >
                                <Copy size={12} />
                              </button>
                            </div>
                          </div>
                          {user.emailVerified && (
                            <span className="flex items-center gap-1 rounded-md bg-success-container px-2 py-0.5 text-[10px] font-bold text-primary">
                              <CheckCircle2 size={12} /> Terverifikasi
                            </span>
                          )}
                        </div>

                        {/* Phone / WA */}
                        <div className="flex items-start justify-between gap-3 rounded-2xl bg-surface-container-low p-3.5">
                          <div>
                            <div className="text-[11px] text-on-surface-variant font-medium flex items-center gap-1">
                              <Phone size={12} className="text-primary" />{' '}
                              <Bilingual {...copy.users.labelPhoneShort} />
                            </div>
                            <div className="font-body text-xs font-semibold text-on-surface mt-0.5 flex items-center gap-2">
                              <span>{user.phone || '-'}</span>
                              {user.phone && (
                                <button
                                  type="button"
                                  onClick={() => handleCopyText(user.phone!, 'No. Telepon')}
                                  className="text-on-surface-variant hover:text-primary"
                                  title={t(copy.users.btnCopyData)}
                                >
                                  <Copy size={12} />
                                </button>
                              )}
                            </div>
                          </div>
                          {user.phone && (
                            <button
                              type="button"
                              onClick={() => setCommChannel('whatsapp')}
                              className="mt-1 flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
                            >
                              <MessageCircle size={13} /> Chat WA
                            </button>
                          )}
                        </div>

                        {/* Gender */}
                        <div className="rounded-2xl bg-surface-container-low p-3.5">
                          <div className="text-[11px] text-on-surface-variant font-medium">
                            <Bilingual {...copy.users.labelGender} />
                          </div>
                          <div className="font-body text-xs font-semibold text-on-surface mt-1">
                            {user.gender === 'FEMALE' ? (
                              <span className="inline-flex items-center gap-1.5 rounded-lg bg-tertiary/15 px-2.5 py-1 font-bold text-tertiary">
                                Akhwat (Perempuan)
                              </span>
                            ) : user.gender === 'MALE' ? (
                              <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 font-bold text-primary">
                                Ikhwan (Laki-laki)
                              </span>
                            ) : (
                              <span className="text-on-surface-variant opacity-60 italic">
                                Belum diisi
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card 2: Wilayah Domisili (Kota/Kabupaten) with Auto-suggest */}
                      <div className="space-y-4 rounded-3xl bg-surface-container-lowest p-5 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-label font-bold text-on-surface flex items-center gap-1.5">
                            <MapPin size={15} className="text-primary" />{' '}
                            <Bilingual {...copy.users.labelDomisili} />
                          </div>
                          {!isEditingDomisili ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedDomisili(currentDomisili);
                                setIsEditingDomisili(true);
                              }}
                              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
                            >
                              <Edit2 size={12} /> <Bilingual {...copy.users.btnEditDomisili} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsEditingDomisili(false)}
                              className="text-xs text-on-surface-variant hover:text-on-surface font-semibold"
                            >
                              <Bilingual {...copy.users.btnCancel} />
                            </button>
                          )}
                        </div>

                        {isEditingDomisili ? (
                          <div className="mt-3 space-y-3">
                            <CityAutocomplete
                              value={selectedDomisili}
                              onChange={val => setSelectedDomisili(val)}
                              placeholder="Ketik nama kota/kabupaten..."
                            />
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsEditingDomisili(false)}
                                className="text-xs"
                              >
                                <Bilingual {...copy.users.btnCancel} />
                              </Button>
                              <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                disabled={isUpdatingUser}
                                onClick={handleSaveDomisili}
                                leftIcon={<Check size={13} />}
                                className="text-xs font-bold active:scale-95"
                              >
                                <Bilingual {...copy.users.btnSaveDomisili} />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="font-body text-xs font-semibold text-on-surface mt-1">
                            {currentDomisili ? (
                              <div className="flex items-center justify-between rounded-2xl bg-surface-container-low p-3.5">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-primary" />
                                  {currentDomisili}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleCopyText(currentDomisili, 'Domisili')}
                                  className="text-on-surface-variant hover:text-primary"
                                  title={t(copy.users.btnCopyData)}
                                >
                                  <Copy size={13} />
                                </button>
                              </div>
                            ) : (
                              <div className="rounded-2xl bg-surface-container-low p-3.5 italic text-on-surface-variant opacity-70">
                                Wilayah domisili belum ditentukan (Klik Ubah untuk melengkapi data).
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card 3: Metadata Akun */}
                    <div className="grid grid-cols-2 gap-4 rounded-3xl bg-surface-container-lowest p-5 text-xs sm:p-6">
                      <div>
                        <div className="text-[10px] text-on-surface-variant font-medium flex items-center gap-1">
                          <Calendar size={12} className="text-primary" />{' '}
                          <Bilingual {...copy.users.registeredDateLabel} />
                        </div>
                        <div className="font-semibold text-on-surface text-xs mt-1">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString('id-ID', {
                                dateStyle: 'long',
                              })
                            : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-on-surface-variant font-medium flex items-center gap-1">
                          <Clock size={12} className="text-primary" />{' '}
                          <Bilingual {...copy.users.detailLastActivity} />
                        </div>
                        <div className="font-semibold text-on-surface text-xs mt-1">
                          {lastActivityDate ? (
                            formatDistanceToNow(new Date(lastActivityDate), language, {
                              addSuffix: true,
                            })
                          ) : (
                            <Bilingual {...copy.users.detailNoActivity} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: PROGRAM & EVALUASI */}
                {activeTab === 'programs' && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-label font-bold text-on-surface flex items-center gap-1.5">
                        <BookOpen size={15} className="text-primary" /> Daftar Program Pembelajaran
                      </span>
                      <span className="text-xs font-bold text-primary bg-primary/10 px-3 py-0.5 rounded-full">
                        {totalProgramsCount} Program
                      </span>
                    </div>

                    {/* Active Enrollments */}
                    {user.enrollments && user.enrollments.length > 0 ? (
                      user.enrollments.map((enr: EnrollmentItem) => {
                        const isActiveProgram = enr.programId === programId;
                        return (
                          <div
                            key={enr.id}
                            className={`space-y-4 rounded-3xl p-5 transition-all ${
                              isActiveProgram
                                ? 'bg-primary/10 shadow-level1'
                                : 'bg-surface-container-lowest'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <h4 className="font-headline font-bold text-on-surface text-sm m-0 leading-snug">
                                  {enr.program?.name}
                                </h4>
                                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                  {enr.program?.code && (
                                    <span className="text-[10px] font-mono font-bold bg-surface-container-high px-2 py-0.5 rounded text-on-surface-variant">
                                      {enr.program.code}
                                    </span>
                                  )}
                                  {enr.program?.cohort && (
                                    <span className="text-[10px] font-semibold text-on-surface-variant bg-surface-container-high px-2 py-0.5 rounded">
                                      Angkatan {enr.program.cohort}
                                    </span>
                                  )}
                                  {isActiveProgram && (
                                    <span className="text-[9px] font-bold text-primary uppercase tracking-wider bg-primary/10 px-2 py-0.5 rounded">
                                      <Bilingual {...copy.users.activeProgramBadge} />
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span
                                className={`text-[10px] font-label font-bold uppercase tracking-wider px-2.5 py-1 rounded-xl shrink-0 ${
                                  enr.status === 'COMPLETED'
                                    ? 'bg-success-container text-primary'
                                    : 'bg-primary/10 text-primary'
                                }`}
                              >
                                {enr.status === 'COMPLETED' ? 'LULUS' : 'SEDANG BELAJAR'}
                              </span>
                            </div>

                            {/* Progress bar */}
                            {typeof enr.progress === 'number' && (
                              <div className="space-y-1.5 rounded-2xl bg-surface-container-low p-3.5">
                                <div className="flex justify-between text-xs text-on-surface-variant font-medium">
                                  <span>Kemajuan Belajar</span>
                                  <span className="font-bold text-on-surface">
                                    {Math.round(enr.progress)}%
                                  </span>
                                </div>
                                <div className="w-full bg-surface-container-high h-2 rounded-full overflow-hidden">
                                  <div
                                    className="bg-primary h-full rounded-full transition-all duration-500"
                                    style={{
                                      width: `${Math.min(100, Math.max(0, enr.progress))}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Render evaluations if this is the active program context */}
                            {isActiveProgram && userEnrollment && (
                              <div className="space-y-3 rounded-2xl bg-surface-container-low p-3.5">
                                <h5 className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest m-0">
                                  <Bilingual {...copy.users.evaluationsSectionHeader} />
                                </h5>
                                {userEnrollment.program?.evaluations &&
                                userEnrollment.program.evaluations.length > 0 ? (
                                  userEnrollment.program.evaluations.map((ev: EvaluationItem) => {
                                    const result = userEnrollment.results?.find(
                                      (r: EvaluationResult) => {
                                        const metadata = (r.metadata || {}) as Record<
                                          string,
                                          unknown
                                        >;
                                        return metadata.evaluationId === ev.id;
                                      }
                                    );
                                    const hasAttempts = !!result;

                                    return (
                                      <div
                                        key={ev.id}
                                        className="flex items-center justify-between rounded-2xl bg-surface-container-lowest p-3.5"
                                      >
                                        <div className="min-w-0 flex-1 pr-3">
                                          <p className="text-xs font-bold text-on-surface truncate m-0">
                                            {ev.title}
                                          </p>
                                          <p className="text-[10px] font-semibold uppercase mt-0.5 m-0">
                                            {result ? (
                                              <span
                                                className={
                                                  (result.score ?? 0) >= ev.passingScore
                                                    ? 'text-primary font-bold'
                                                    : 'text-error font-bold'
                                                }
                                              >
                                                Skor: {Math.round(result.score ?? 0)}/100 (
                                                {(result.score ?? 0) >= ev.passingScore
                                                  ? 'LULUS'
                                                  : 'GAGAL'}
                                                )
                                              </span>
                                            ) : (
                                              <span className="text-on-surface-variant/50">
                                                <Bilingual {...copy.users.notAttemptedStatus} />
                                              </span>
                                            )}
                                          </p>
                                        </div>
                                        {hasAttempts && (
                                          <button
                                            type="button"
                                            onClick={() => handleResetAttempts(ev.id)}
                                            disabled={isResetting}
                                            className="shrink-0 rounded-xl p-2 text-primary transition-all hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                                            title={t(copy.users.resetAttemptsTooltip)}
                                          >
                                            <RotateCcw size={15} />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <p className="text-xs text-on-surface-variant/60 italic m-0">
                                    Tidak ada evaluasi pada program ini.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl bg-surface-container-low p-6 text-center text-xs italic text-on-surface-variant">
                        <Bilingual {...copy.users.detailNoEnrollments} />
                      </div>
                    )}

                    {/* Pending Registrations if any */}
                    {user.registrations &&
                      user.registrations
                        .filter(
                          (reg: RegistrationItem) =>
                            !user.enrollments?.some(
                              (e: EnrollmentItem) => e.programId === reg.program?.id
                            )
                        )
                        .map((reg: RegistrationItem) => (
                          <div
                            key={reg.id}
                            className="flex items-center justify-between rounded-2xl bg-surface-container-low p-4"
                          >
                            <div>
                              <div className="text-xs font-bold text-on-surface">
                                {reg.program?.name}
                              </div>
                              <div className="text-[11px] text-on-surface-variant mt-0.5">
                                Terdaftar: {new Date(reg.registeredAt).toLocaleDateString('id-ID')}
                              </div>
                            </div>
                            <span className="text-[10px] font-bold uppercase bg-surface-container-high px-2.5 py-1 rounded-lg text-on-surface-variant">
                              {reg.status}
                            </span>
                          </div>
                        ))}
                  </div>
                )}

                {/* TAB 3: LOG AKTIVITAS */}
                {activeTab === 'activities' && (
                  <div className="space-y-4 animate-fadeIn">
                    <span className="text-xs font-label font-bold text-on-surface flex items-center gap-1.5">
                      <History size={15} className="text-primary" /> Riwayat Aktivitas Akun
                    </span>

                    <div className="space-y-2.5">
                      {user.auditLogs && user.auditLogs.length > 0 ? (
                        user.auditLogs.map((log: AuditLogItem) => (
                          <div
                            key={log.id}
                            className="space-y-1.5 rounded-2xl bg-surface-container-lowest p-4"
                          >
                            <div className="flex justify-between items-start">
                              <div className="font-bold text-xs text-on-surface">{log.action}</div>
                              <div className="text-[11px] text-on-surface-variant font-mono">
                                {formatDistanceToNow(new Date(log.createdAt), language, {
                                  addSuffix: true,
                                })}
                              </div>
                            </div>
                            <div className="text-xs text-on-surface-variant">
                              <Bilingual {...copy.users.entityLabel} />: {log.entity}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-2xl bg-surface-container-low p-6 text-center text-xs italic text-on-surface-variant">
                          <Bilingual {...copy.users.detailNoActivity} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* WhatsApp & Email Communication Modal */}
      {commRecipient && commChannel && (
        <CommunicationModal
          recipient={commRecipient}
          initialChannel={commChannel}
          initialTemplateId={commTemplateId}
          onClose={() => {
            setCommChannel(null);
            setCommTemplateId(undefined);
          }}
        />
      )}
      {user && isPasswordResetOpen && (
        <ParticipantPasswordResetDialog
          participantName={user.name}
          participantEmail={user.email}
          onClose={() => setIsPasswordResetOpen(false)}
          onSend={handlePasswordReset}
          onOpenCommunication={sharePasswordResetInstructions}
        />
      )}
    </>
  );
}
